<template>
  <div>
    <div class="d-flex justify-content-end">
      <div class="d-flex justify-content-between button-group">
        <img v-if="currentRoutePermissions.add_salesorder"
             src="@/assets/images/common-icons/icon_create.svg"
             :title="'Create sales order'" id="creating">
        <img v-if="currentRoutePermissions.delete_salesorder"
             src="@/assets/images/common-icons/icon_delete.svg"
             @click="deleteSalesOrders"
             :title="'Delete sales orders'">
        <img src="@/assets/images/common-icons/icon_clear.png" @click="cleanFilters"
             :title="$t('buttons.common.clear_filters')">
        <img src="@/assets/images/common-icons/icon_search.svg" @click="search"
             :title="$t('buttons.common.search')">
      </div>
    </div>
    <b-popover target="creating" placement="bottom" ref="createWithStatus" :show.sync="popoverCreateWithStatus">
      <div class="d-flex">
        <b-btn variant="warning" size="sm" @click="create('forecast')">Forecast</b-btn>
        <b-btn variant="primary" size="sm" @click="create('final')" class="ml-1">Final</b-btn>
      </div>
    </b-popover>
    <div class="search-form">
      <div class="search-form__filters">
        <b-row>
          <b-col>
            <label>{{$t('labels.sale_order.order_date')}}</label>
            <range-date-picker v-model="searchData.orderDate" range lang="en"
                               range-separator="/" :first-day-of-week="1"
                               placeholder="Select order date"></range-date-picker>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.creator')}}</label>
            <multiselect v-model="searchData.creators" :max-height="200"
                         placeholder="Choose creators" label="username" track-by="username"
                         :options="creators" :multiple="true"
                         :close-on-select="false"></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.descriptor')}}</label>
            <input class="form-control" placeholder="Enter descriptor"
                   v-model="searchData.descriptor" @keyup.enter="search">
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <label>{{$t('labels.common.delivery_date')}}</label>
            <range-date-picker v-model="searchData.deliveryDate" range lang="en"
                               range-separator="/" :first-day-of-week="1"
                               placeholder="Select delivery date"></range-date-picker>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.buyer')}}</label>
            <multiselect v-model="searchData.buyers" :max-height="200"
                         placeholder="Choose buyers" label="name" track-by="name"
                         :options="buyers" :multiple="true"
                         :close-on-select="false"></multiselect>
          </b-col>
          <b-col class="checkbox-wrapper">
            <label for="not_ready_for_invoice">{{$t('labels.sale_order.not_ready_for_invoice')}}</label>
            <input type="checkbox" id="not_ready_for_invoice"
                   v-model="searchData.not_ready_for_invoice" @change="toggleReadingForInvoicesStatement">
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <label>{{$t('labels.common.status')}}</label>
            <multiselect v-model="searchData.statuses" :max-height="200"
                         placeholder="Choose order statuses" label="text" track-by="text"
                         :options="statuses" :multiple="true"
                         :close-on-select="false"></multiselect>
          </b-col>
          <b-col>
            <label>{{$t('labels.common.delivery_method')}}</label>
            <multiselect v-model="searchData.delivery_methods" :max-height="200"
                         placeholder="Choose delivery methods" label="name" track-by="name"
                         :options="delivery_methods" :multiple="true"
                         :close-on-select="false"></multiselect>
          </b-col>
          <b-col class="d-flex">
            <b-col class="padding-left-0">
              <label>{{$t('labels.sale_order.wo_price')}}</label>
              <multiselect v-model="searchData.zero_price_item_exists" :max-height="200"
                           placeholder="" label="text" track-by="text" :show-labels="false"
                           :options="isWithoutPriceFilterItems" :multiple="false"
                           :close-on-select="true"
                           :disabled="searchData.not_ready_for_invoice"></multiselect>
            </b-col>
            <b-col class="padding-right-0">
              <label>{{$t('labels.sale_order.unbound_packages')}}</label>
              <multiselect v-model="searchData.free_movement_data_exist" :max-height="200"
                           placeholder="" label="text" track-by="text" :show-labels="false"
                           :options="isWithoutPriceFilterItems" :multiple="false"
                           :close-on-select="true"
                           :disabled="this.searchData.not_ready_for_invoice"></multiselect>
            </b-col>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="4">
            <label>{{$t('labels.sale_order.stock_address')}}</label>
            <multiselect v-model="searchData.stock_addresses" :max-height="200"
                         placeholder="Choose stock address" label="name" track-by="name"
                         :options="stock_addresses" :multiple="true"
                         :close-on-select="false"></multiselect>
          </b-col>
        </b-row>
      </div>
      <div>
        <b-row>
          <b-col cols="4">
            <input class="form-control"
                   placeholder="Filter..."
                   @input="setQuickFilter">
          </b-col>
          <b-col cols="4" offset="4" class="vertical-center-container justify-content-end">
            <label for="pagination-toggle" class="margin-right-5">Pagination</label>
            <input id="pagination-toggle" type="checkbox"
                   title="this checkbox needed for toggle pagination bar" v-model="paginationState"
                   @change="togglePagination">
            <save-columns-btn :grid="gridOptions" :grid-name="gridName"
                              class="margin-left-5"></save-columns-btn>
          </b-col>
        </b-row>
      </div>
    </div>
    <div>
      <b-row>
        <b-col>
          <ag-grid-vue class="ag-theme-balham" :gridOptions="gridOptions" :style="gridHeight"
                       :rowData="salesOrders" :columnDefs="columns" @rowDoubleClicked="goToSalesOrder"
                       :postProcessPopup="postProcessPopup">
          </ag-grid-vue>
        </b-col>
      </b-row>
      <b-row class="pagination__wrapper" v-if="paginationState">
        <b-col cols="5">
          <div class="pagination-info">{{countOfViewSalesOrders}} {{$t('common_content.of')}} {{salesOrdersCount}}
          </div>
        </b-col>
        <b-col cols="7">
          <b-pagination size="md" :total-rows="salesOrdersCount" :per-page="searchObject.limit"
                        v-model="currentPaginateIndex"
                        @input="changePaginateState"
          ></b-pagination>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
  import { AgGridVue } from 'ag-grid-vue';
  import { mapState, mapGetters } from 'vuex';
  import RangeDatePicker from 'vue2-datepicker';
  import Multiselect from 'vue-multiselect';
  import { notificationsRenderer } from '@/helpers/renderHelpers';
  import SaveColumnsBtn from '@/components/common/SaveColumnsBtn';

  import {
    cleanFilters,
    saveFilters,
    setFilters,
    checkSavedFilterData,
    getArrayByProperty,
    setNotifyData,
    transformDateFromDateRangeToStringFormatByIndex
  } from '@/helpers/commonHelpers';
  import { gridMixins } from '@/mixins'

  export default {
    mixins: [gridMixins],
    components: {
      RangeDatePicker,
      Multiselect,
      'ag-grid-vue': AgGridVue,
      SaveColumnsBtn
    },
    data() {
      return {
        popoverCreateWithStatus: false,
        paginationState: true,
        statuses: [{text: 'Forecast', key: 'forecast'}, {text: 'Final', key: 'final'}, {
          text: 'Completed',
          key: 'completed'
        }, {text: 'Canceled', key: 'canceled'}],
        isWithoutPriceFilterItems: [{text: 'Only', key: true,}, {text: 'Besides', key: false}],
        isUnBoundPackagesFilterItems: [{text: 'Only', key: true,}, {text: 'Besides', key: false}],
        searchData: {
          orderDate: null,
          deliveryDate: null,
          statuses: [],
          creators: [],
          buyers: [],
          delivery_methods: [],
          descriptor: null,
          stock_addresses: [],
          not_ready_for_invoice: false,
          zero_price_item_exists: [],
          free_movement_data_exist: []
        },
        searchObject: {
          limit: 100,
          offset: 0
        },
        currentPaginateIndex: 1
      }
    },
    methods: {
      create(status) {
        this.popoverCreateWithStatus = false;
        this.$router.push({path: `/sales-orders/${status}/new`});
      },
      deleteSalesOrders() {
        const ids = getArrayByProperty(this.gridOptions.api.getSelectedRows(), 'id');
        if (ids.length) {
          this.$http.delete(`sales/salesorders/`, {body: {ids}}).then((response) => {
            this.$notify(setNotifyData('Success', response.body, 'success'));
            this.currentPaginateIndex = 1;
            this.$store.dispatch('salesOrders/loadSalesOrders', this.searchObject);
          }).catch((error) => {
            notificationsRenderer(error.data, 'error');
          });
        } else {
          this.$notify(setNotifyData('Select sales orders for removing', '', 'warn'));
        }
      },
      cleanFilters() {
        cleanFilters(this.searchData);
      },
      search() {
        this.currentPaginateIndex = 1;
        this.searchObject = {
          ...this.searchObject,
          created_date_between_after: transformDateFromDateRangeToStringFormatByIndex(this.searchData.orderDate, 0),
          created_date_between_before: transformDateFromDateRangeToStringFormatByIndex(this.searchData.orderDate, 1),
          delivery_date_between_after: transformDateFromDateRangeToStringFormatByIndex(this.searchData.deliveryDate, 0),
          delivery_date_between_before: transformDateFromDateRangeToStringFormatByIndex(this.searchData.deliveryDate, 1),
          status_name: getArrayByProperty(this.searchData.statuses, 'key').toString(),
          user: getArrayByProperty(this.searchData.creators, 'id').toString(),
          partner: getArrayByProperty(this.searchData.buyers, 'id').toString(),
          delivery_method: getArrayByProperty(this.searchData.delivery_methods, 'id').toString(),
          descriptor: this.searchData.descriptor,
          stock_addresses: getArrayByProperty(this.searchData.stock_addresses, 'id').toString(),
          not_ready_for_invoice: this.searchData.not_ready_for_invoice,
          zero_price_item_exists: this.searchData.zero_price_item_exists && this.searchData.zero_price_item_exists.key,
          free_movement_data_exist: this.searchData.free_movement_data_exist && this.searchData.free_movement_data_exist.key
        };

        this.$store.dispatch('salesOrders/loadSalesOrders', this.searchObject);
        saveFilters('salesOrders', this.searchData);
      },
      togglePagination() {
        if (!this.paginationState) {
          delete this.searchObject.limit;
          delete this.searchObject.offset;
        } else {
          this.searchObject.limit = 100;
          this.searchObject.offset = 0;
        }
        this.search();
      },
      toggleReadingForInvoicesStatement() {
        if (this.searchData.not_ready_for_invoice) {
          this.searchData.zero_price_item_exists = null;
          this.searchData.free_movement_data_exist = null;
        }
      },
      setQuickFilter(e) {
        this.gridOptions.api.setQuickFilter(e.target.value);
      },
      changePaginateState() {
        const searchObject = {...this.searchObject, offset: (this.currentPaginateIndex - 1) * this.searchObject.limit};
        this.$store.dispatch('salesOrders/loadSalesOrders', searchObject);
      },
      goToSalesOrder(params) {
        if (params.data.id) {
          this.$router.push({path: `/sales-orders/${params.data.id}`});
        }
      }
    },
    computed: {
      ...mapState('generic', ['currentRoutePermissions']),
      ...mapGetters('woodware', {
        delivery_methods: 'delivery_methods',
        creators: 'users',
        stock_addresses: 'stock_addresses'
      }),
      ...mapGetters('partners', {
        buyers: 'customers'
      }),
      ...mapState('salesOrders', ['gridName']),
      ...mapGetters('salesOrders', {
        gridOptions: 'gridOptions',
        salesOrders: 'salesOrders',
        columns: 'columns',
        salesOrdersCount: 'salesOrdersCount'
      }),
      countOfViewSalesOrders() {
        return this.salesOrders.length;
      },
      gridHeight() {
        if (window.innerHeight > 1024) {
          return {height: 'calc(100vh - 460px)'};
        } else {
          return {height: '550px'};
        }
      }
    },
    created() {
      this.$store.dispatch('partners/getCustomers');
      this.$store.cache.dispatch('woodware/getDeliveryMethods');
      this.$store.cache.dispatch('woodware/getUsers');
      this.$store.cache.dispatch('woodware/getStockAddresses');
      if (checkSavedFilterData('salesOrders')) {
        this.searchData = setFilters('salesOrders');
      }
      this.search();
    },
  }
</script>




// WEBPACK FOOTER //
// src/components/pages/modules/sales/orders/SalesOrdersList.vue